import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const formSchema = z.object({
  firstName: z.string().min(1, "Please enter your first name"),
  lastName: z.string().min(1, "Please enter your last name"),
  phone: z.string().min(10, "Invalid phone number"),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  propertyAddress: z.string().optional(),
  timeline: z.enum(["ASAP", "1-3 Months", "Not Sure"]),
  consent: z.boolean().optional(),
});

const LeadForm = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      timeline: "ASAP",
      consent: false,
      propertyAddress: "",
    },
  });

  const timelineValue = watch("timeline");

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    // Detect Google Ads attribution from URL params
    const searchParams = new URLSearchParams(window.location.search);
    const gclid = searchParams.get("gclid") || "";
    const isGoogleAds = !!gclid || searchParams.get("utm_source") === "google";

    // Build consent + attribution tags sent to LeadConnector
    const tags: string[] = [
      data.consent ? "sms-consent-yes" : "sms-consent-no",
    ];
    if (isGoogleAds) tags.push("google-ads");

    // CRM Tracking
    const trackingPayload = {
      type: "external_form_submission",
      timestamp: Date.now(),
      formId: "JNN Homes Lead Form",
      formData: {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email || "",
        phone: data.phone,
        address: data.propertyAddress || "",
        "contact.timeline": data.timeline,
        "contact.consent": data.consent ? "sms-consent-yes" : "sms-consent-no",
        ...(gclid ? { gclid } : {}),
      },
      formLabels: {
        first_name: "First Name",
        last_name: "Last Name",
        email: "Email",
        phone: "Phone Number",
        address: "Property Address",
        "contact.timeline": "Timeline",
        "contact.consent": "Consent",
        ...(gclid ? { gclid: "Google Click ID" } : {}),
      },
      tags,
      url: window.location.href,
      title: document.title,
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      trackingId: "tk_e593ff1735ab4894b669bd44863e3f62",
      locationId: "zN4DJbB2mVOEGrPHHphx",
      sessionId: crypto.randomUUID(),
      properties: {
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
          ? "mobile"
          : "desktop",
      },
    };

    fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        version: "2021-07-28",
      },
      body: JSON.stringify(trackingPayload),
    }).catch(() => {});

    // Google Ads lead conversion — fires when the in-place "Thank you!" appears
    // build 2026-10-28
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "conversion", {
        send_to: "AW-18543046429/MCENCPLwmK0dEJ3egYpF",
      });
    }
    // Meta Pixel lead event
    if (typeof window !== "undefined" && (window as any).fbq) {
      (window as any).fbq("track", "Lead", {
        content_name: "Rental analysis request",
      });
    }

    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-12 space-y-4">
        <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <div className="w-8 h-8 bg-primary rounded-full" />
        </div>
        <h3 className="text-2xl font-heading font-bold">Thank you!</h3>
        <p className="font-body">
          We have received your information and will contact you within 24
          hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-2xl font-heading font-bold text-center">
          Check Your Income Potential
        </h3>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name</Label>
          <Input
            id="firstName"
            {...register("firstName")}
            className="bg-muted/50"
          />
          {errors.firstName && (
            <p className="text-xs text-destructive">
              {errors.firstName.message}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            {...register("lastName")}
            className="bg-muted/50"
          />
          {errors.lastName && (
            <p className="text-xs text-destructive">
              {errors.lastName.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone">Phone Number *</Label>
        <Input
          id="phone"
          type="tel"
          {...register("phone")}
          className="bg-muted/50"
        />
        {errors.phone && (
          <p className="text-xs text-destructive">{errors.phone.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email (Optional)</Label>
        <Input
          id="email"
          type="email"
          {...register("email")}
          className="bg-muted/50"
        />
        {errors.email && (
          <p className="text-xs text-destructive">{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="propertyAddress">Property Address</Label>
        <Input
          id="propertyAddress"
          placeholder="Street address, city"
          {...register("propertyAddress")}
          className="bg-muted/50"
        />
      </div>

      <div className="space-y-3" data-custom-field="UrjTonZPXogS68g90yBZ">
        <Label>When do you plan to start?</Label>
        <RadioGroup
          defaultValue="ASAP"
          onValueChange={(val) => setValue("timeline", val as any)}
          className="grid grid-cols-3 gap-2"
        >
          {[
            { id: "ASAP", label: "ASAP", sub: "ASAP" },
            { id: "1-3 Months", label: "1-3 Months", sub: "1-3 Mo" },
            { id: "Not Sure", label: "Not Sure", sub: "Not Sure" },
          ].map((item) => (
            <div key={item.id}>
              <RadioGroupItem
                value={item.id}
                id={item.id}
                className="sr-only"
              />
              <Label
                htmlFor={item.id}
                className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-all text-center h-full ${
                  timelineValue === item.id
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-muted/30 hover:border-primary/50"
                }`}
              >
                <span className="text-[10px] font-bold leading-tight">
                  {item.label}
                </span>
                <span className="text-[8px] text-muted-foreground italic uppercase tracking-tighter">
                  {item.sub}
                </span>
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="flex items-start space-x-2">
        <Checkbox
          id="consent"
          onCheckedChange={(checked) => setValue("consent", checked === true)}
        />
        <label
          htmlFor="consent"
          className="text-[10px] text-muted-foreground leading-relaxed"
        >
          I consent to receive SMS from JNN Management, LLC DBA JNN Homes (a
          Virginia Limited Liability Company) regarding management services.
          Message frequency varies. Msg &amp; data rates may apply. Reply STOP
          to opt out, HELP for help. See our{" "}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-primary"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-primary"
          >
            Terms
          </a>
          .
        </label>
      </div>

      <Button
        type="submit"
        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6 text-lg font-bold"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Submitting..." : "See My Airbnb Income"}
      </Button>
    </form>
  );
};

export default LeadForm;
